/**
 * EmployeeUtilizationPage — Phase 4c+.
 *
 * Tages-Übersicht der Mitarbeiter-Auslastung. Pro Mitarbeiter werden
 * gestempelte Stunden, aktive Timer und die Aufträge angezeigt, an denen
 * gearbeitet wurde. Perfekt für die morgendliche Werkstatt-Planung.
 *
 * Route: /dealer/employee-utilization
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Activity, ChevronLeft, ChevronRight, Wrench, Clock, Timer, ListTodo, Mail, AlertTriangle, FileWarning, Inbox, Send, RotateCcw } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const FN_LABEL = {
    verkaeufer: "Verkauf",
    verkaufsleiter: "Verkaufsleitung",
    kundendienst: "Kundendienst",
    mechaniker: "Mechanik",
    filialleiter: "Filialleitung",
    gebietsleitung: "Gebietsleitung",
    disposition: "Disposition",
    buchhaltung: "Buchhaltung",
    geschaeftsleitung: "Geschäftsleitung",
};
const SPEC_LABEL = {
    aufbereitung: "Aufbereitung",
    lehrling: "Lehrling",
    mechanik: "Mechanik",
    verschleiss: "Verschleiß",
};

const fmtH = (mins) => {
    if (!mins) return "0 h";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m} min`;
};

const shiftDate = (iso, days) => {
    const d = new Date(iso + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

export default function EmployeeUtilizationPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/dealer/employee-utilization?date=${date}`);
            setData(r.data);
        } catch (_e) {
            setData(null);
        }
        setLoading(false);
    }, [date]);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b" && user.role !== "admin" && user.role !== "zweiradverkaeufer") navigate("/");
        if (user) load();
    }, [user, navigate, load]);

    if (!user) return <div className="p-8">Lade…</div>;

    const today = new Date().toISOString().slice(0, 10);
    const isToday = date === today;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="employee-utilization-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dealer/workshop-orders" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-wo">
                        <ArrowLeft className="w-3 h-3" /> Werkstatt-Aufträge
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <Activity className="w-7 h-7 text-[#FFCC00]" />
                            <div>
                                <h1 className="font-display font-black text-4xl md:text-5xl">Mitarbeiter-Auslastung</h1>
                                <p className="text-sm text-gray-300 mt-1">Wer hat heute schon gestempelt? Welche Aufträge laufen?</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-4">
                {/* Datum-Navigation */}
                <div className="bg-white border border-gray-200 rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid="utilization-date-nav">
                    <button onClick={() => setDate(shiftDate(date, -1))} className="p-2 rounded-sm hover:bg-gray-100" data-testid="util-prev-day">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="rounded-sm border border-gray-300 px-3 py-1.5 text-sm font-mono-num"
                        data-testid="util-date-input"
                    />
                    <button onClick={() => setDate(shiftDate(date, 1))} className="p-2 rounded-sm hover:bg-gray-100" data-testid="util-next-day">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    {!isToday && (
                        <button onClick={() => setDate(today)} className="text-xs uppercase tracking-wider font-bold text-blue-700 underline" data-testid="util-today">
                            → Heute
                        </button>
                    )}
                    <div className="ml-auto text-sm text-gray-700">
                        <strong className="font-mono-num">{new Date(date + "T12:00:00Z").toLocaleDateString("de-DE", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</strong>
                    </div>
                </div>

                {/* Summary Cards */}
                {data?.summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="utilization-summary">
                        <SummaryCard label="Mitarbeiter aktiv" value={data.summary.total_employees} icon={<Users className="w-4 h-4" />} tone="default" testid="sum-employees" />
                        <SummaryCard label="Minuten total" value={data.summary.total_minutes} icon={<Clock className="w-4 h-4" />} tone="default" testid="sum-minutes" />
                        <SummaryCard label="Stunden total" value={data.summary.total_hours} icon={<Activity className="w-4 h-4" />} tone="success" testid="sum-hours" />
                        <SummaryCard label="Laufende Timer" value={data.summary.active_timers} icon={<Timer className="w-4 h-4" />} tone={data.summary.active_timers > 0 ? "warn" : "muted"} testid="sum-active" />
                    </div>
                )}
                {data?.summary?.absent_count > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-sm px-4 py-2 text-sm text-purple-900" data-testid="util-absent-banner">
                        <strong>{data.summary.absent_count}</strong> Mitarbeiter heute abwesend (Urlaub/Krank etc.) — bei der Zuteilung von Leads/Aufgaben berücksichtigt.
                    </div>
                )}

                {data?.todo && <OpenWorkPanel todo={data.todo} />}

                {/* Mitarbeiter-Liste */}
                {loading ? (
                    <div className="text-center py-8 text-gray-500">Lade…</div>
                ) : (data?.items?.length || 0) === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="util-empty">
                        <Users className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                        <p className="font-bold text-gray-700">Keine Stempel-Zeiten an diesem Tag</p>
                        <p className="text-sm text-gray-500 mt-1">Wenn deine Mitarbeiter heute noch nicht gestempelt haben, taucht hier nichts auf. Lege Aufträge an und nutze die Stempeluhr im jeweiligen Auftrag.</p>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="utilization-list">
                        {data.items.map((emp) => (
                            <EmployeeRow key={emp.user_id} emp={emp} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function OpenWorkPanel({ todo }) {
    const cards = [
        { key: "open_tasks", label: "Offene Aufgaben / ToDos", value: todo.open_tasks, icon: <ListTodo className="w-4 h-4" />, to: "/dashboard", tone: todo.open_tasks > 0 ? "warn" : "muted", sub: todo.overdue_tasks > 0 ? `${todo.overdue_tasks} überfällig` : (todo.unassigned_tasks > 0 ? `${todo.unassigned_tasks} ohne Zuweisung` : "") },
        { key: "open_leads", label: "Unbearbeitete Leads", value: todo.open_leads, icon: <Inbox className="w-4 h-4" />, to: "/dealer/leads", tone: todo.open_leads > 0 ? "warn" : "muted" },
        { key: "unhandled_emails", label: "Unbearbeitete Mails", value: todo.unhandled_emails, icon: <Mail className="w-4 h-4" />, to: "/dealer/leasingrueckgabe", tone: todo.unhandled_emails > 0 ? "warn" : "muted" },
        { key: "open_workorders", label: "Offene Werkstattaufträge", value: todo.open_workorders, icon: <Wrench className="w-4 h-4" />, to: "/dealer/workshop-orders", tone: todo.open_workorders > 0 ? "default" : "muted" },
        { key: "leasing_due_30", label: "Leasing fällig < 30 Tage", value: todo.leasing_due_30, icon: <RotateCcw className="w-4 h-4" />, to: "/dealer/leasingrueckgabe", tone: todo.leasing_due_30 > 0 ? "warn" : "muted", sub: `${todo.leasing_open} offen gesamt` },
        { key: "leasing_drafts", label: "Mail-Entwürfe zur Freigabe", value: todo.leasing_drafts, icon: <Send className="w-4 h-4" />, to: "/dealer/leasingrueckgabe", tone: todo.leasing_drafts > 0 ? "warn" : "muted" },
    ];
    const tones = {
        default: "bg-white border-gray-200 hover:border-gray-400",
        warn: "bg-amber-50 border-amber-300 hover:border-amber-400",
        muted: "bg-gray-50 border-gray-200 text-gray-400",
    };
    return (
        <div className="bg-[#111827] rounded-sm p-4" data-testid="util-open-work">
            <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-white">Heute offen · noch zu erledigen</span>
                <span className="ml-auto text-xs text-gray-300">Gesamt offen: <strong className="text-[#FFCC00] font-mono-num">{todo.total}</strong></span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {cards.map((c) => (
                    <Link key={c.key} to={c.to} className={`border rounded-sm p-3 transition-colors ${tones[c.tone]}`} data-testid={`util-todo-${c.key}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                            <span className={c.tone === "muted" ? "text-gray-400" : "text-gray-600"}>{c.icon}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wider leading-tight">{c.label}</span>
                        </div>
                        <div className="font-display font-black text-2xl font-mono-num leading-none">{c.value}</div>
                        {c.sub && <div className="text-[10px] text-gray-500 mt-0.5">{c.sub}</div>}
                    </Link>
                ))}
            </div>
        </div>
    );
}

function SummaryCard({ label, value, icon, tone = "default", testid }) {
    const tones = {
        default: "bg-white border-gray-200",
        success: "bg-green-50 border-green-200",
        warn: "bg-amber-50 border-amber-200",
        muted: "bg-gray-50 border-gray-200 text-gray-500",
    };
    return (
        <div className={`border rounded-sm p-4 ${tones[tone]}`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-1">
                <span className="text-gray-500">{icon}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{label}</span>
            </div>
            <div className="font-display font-black text-3xl text-[#111827] font-mono-num leading-none">{value}</div>
        </div>
    );
}

function EmployeeRow({ emp }) {
    const fn = FN_LABEL[emp.function] || "—";
    const spec = SPEC_LABEL[emp.specialty];
    const isMechaniker = emp.function === "mechaniker";
    const toneBg = emp.active_timer_count > 0
        ? "bg-amber-50 border-amber-300"
        : emp.minutes_grand_total > 0
            ? "bg-white border-gray-200"
            : "bg-gray-50 border-gray-200 opacity-70";
    return (
        <div className={`border rounded-sm p-4 ${toneBg}`} data-testid={`util-row-${emp.user_id}`}>
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-black text-lg">{emp.name}</span>
                        {emp.is_dealer_self && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm">Inhaber</span>
                        )}
                        {!emp.has_login && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-sm">Ohne Login</span>
                        )}
                        {emp.function && (
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${isMechaniker ? "bg-amber-100 text-amber-900" : "bg-blue-100 text-blue-900"}`}>
                                <Wrench className="w-3 h-3 inline mr-0.5" /> {fn}
                                {isMechaniker && spec && <span className="ml-1">· {spec}</span>}
                            </span>
                        )}
                        {emp.active_timer_count > 0 && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-red-100 text-red-900 animate-pulse" data-testid={`util-active-${emp.user_id}`}>
                                <Timer className="w-3 h-3 inline mr-0.5" /> {emp.active_timer_count} Timer läuft
                            </span>
                        )}
                        {emp.absent && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-purple-100 text-purple-900" data-testid={`util-absent-${emp.user_id}`}>
                                Abwesend{emp.absence_label ? ` · ${emp.absence_label}` : ""}{emp.absent_until ? ` bis ${emp.absent_until}` : ""}
                            </span>
                        )}
                        {emp.open_tasks > 0 && (
                            <Link to="/dashboard" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-100 text-amber-900 hover:bg-amber-200" data-testid={`util-tasks-${emp.user_id}`}>
                                <ListTodo className="w-3 h-3 inline mr-0.5" /> {emp.open_tasks} offene Aufgabe{emp.open_tasks === 1 ? "" : "n"}
                            </Link>
                        )}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Heute</div>
                    <div className="font-mono-num font-black text-2xl">{fmtH(emp.minutes_grand_total)}</div>
                    {emp.minutes_active > 0 && (
                        <div className="text-[10px] text-amber-700 font-bold">
                            davon {fmtH(emp.minutes_active)} laufend
                        </div>
                    )}
                </div>
            </div>
            {emp.orders?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Aufträge ({emp.orders.length})</div>
                    <div className="space-y-1">
                        {emp.orders.map((o) => (
                            <Link
                                key={o.id}
                                to={`/dealer/workshop-orders?focus=${o.id}`}
                                className="flex items-center justify-between gap-3 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-sm text-sm border border-gray-200"
                                data-testid={`util-order-${emp.user_id}-${o.id}`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="font-mono-num font-bold">{o.order_number}</span>
                                    <span className="text-gray-500 text-xs truncate">{o.vehicle}</span>
                                    <span className="text-[9px] uppercase tracking-wider bg-white border border-gray-300 px-1 py-0.5 rounded-sm text-gray-600">{o.status}</span>
                                </div>
                                <span className="font-mono-num font-bold text-gray-700 shrink-0">{fmtH(o.minutes)}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
